/**
 * Read-projection: map v1 agrichemical / pest / vet / spraying records onto modern event envelopes.
 *
 * Follow-up: pumps v2 (`features-v2/water-resources/.../wr-asset.js`) still POSTs sensor command
 * audits to `…/agrichemical` with `new.type: 'command'`. Migrate those writers to `createEvent`
 * (`POST /v2/events`) with type `PUMP_COMMAND_EVENT_TYPE` (`pumpCommand`) instead of agrichemical.
 */

import { CHEMICAL_EVENT_TYPES, REPORT_READ_EVENT_TYPES } from './asset-event-types'
import { normalizeEventTime } from './build-asset-event'

/** Target stored type for pump/sensor command audits (legacy + future /v2/events writes). */
export const PUMP_COMMAND_EVENT_TYPE = 'pumpCommand'

const CHEM_SPRAY_APPLY = CHEMICAL_EVENT_TYPES.chemSprayApply
const APPLICATION_BRANCH = CHEM_SPRAY_APPLY.branch
const ANIMAL_TREATMENT_TYPE = REPORT_READ_EVENT_TYPES.animalTreatment

const asId = value => {
    if (value == null || value === '') return null
    if (typeof value === 'object' && value.id != null) return asId(value.id)
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

const asIdList = values => {
    if (!Array.isArray(values)) return []
    return [...new Set(values.map(asId).filter(id => id != null))]
}

export const paddockIdsFromLegacyNew = neu => asIdList(neu?.paddocks)

export const rowMatchesLegacyPaddocks = (row, paddockIds) => {
    if (!paddockIds || !Array.isArray(paddockIds) || paddockIds.length === 0) return true
    const wanted = new Set(paddockIds.map(asId).filter(id => id != null))
    if (!wanted.size) return true
    const have = paddockIdsFromLegacyNew(row?.new)
    return have.some(id => wanted.has(id))
}

export const rowMatchesLegacyAsset = (row, assetId) => {
    if (assetId == null || assetId === '') return true
    const want = asId(assetId)
    if (want == null) return true
    return asId(row?.new?.asset) === want
}

const peopleFromAgri = neu => {
    const out = {}
    if (neu.applicatorName || neu.applicatorEmail || neu.applicatorPhone) {
        out.operator = {
            name: neu.applicatorName || '',
            email: neu.applicatorEmail || '',
            phone: neu.applicatorPhone || '',
        }
    }
    if (neu.supervisorName || neu.supervisorEmail || neu.supervisorPhone) {
        out.supervisor = {
            name: neu.supervisorName || '',
            email: neu.supervisorEmail || '',
            phone: neu.supervisorPhone || '',
        }
    }
    if (neu.clientName || neu.clientEmail || neu.clientPhone) {
        out.client = {
            name: neu.clientName || '',
            email: neu.clientEmail || '',
            phone: neu.clientPhone || '',
        }
    }
    return out
}

const peopleFromPest = neu => {
    const out = {}
    if (neu.baitUserName || neu.baitUserEmail || neu.baitUserPhone) {
        out.operator = {
            name: neu.baitUserName || '',
            email: neu.baitUserEmail || '',
            phone: neu.baitUserPhone || '',
        }
    }
    if (neu.supervisorName || neu.supervisorEmail || neu.supervisorPhone) {
        out.supervisor = {
            name: neu.supervisorName || '',
            email: neu.supervisorEmail || '',
            phone: neu.supervisorPhone || '',
        }
    }
    if (neu.clientName || neu.clientEmail || neu.clientPhone) {
        out.client = {
            name: neu.clientName || '',
            email: neu.clientEmail || '',
            phone: neu.clientPhone || '',
        }
    }
    return out
}

const mapPumpCommand = neu => ({
    description: neu.desc || '',
    sensorId: neu.sensor || '',
    group: neu.group || '',
})

const mapAgriApplication = neu => ({
    kind: {
        applicationType: 'agrichemical',
        cropType: neu.cropType || '',
        targetPests: [],
        targetWeeds: [],
    },
    quantity: {
        areaSize: neu.areaSize ?? '',
        totalQtyUsed: neu.productAmount ?? '',
    },
    schedule: {
        startTime: neu.startTime || '',
        finishTime: neu.finishTime || '',
    },
    withhold: {
        whp: neu.whp ?? '',
    },
    conditionsBefore: {
        temperature: neu.temperature ?? '',
        humidity: neu.humidity ?? '',
        windSpeed: neu.windSpeed ?? '',
        windDirection: neu.windDirection || '',
    },
    productId: neu.productId ?? null,
    equipment: neu.setupId != null ? { setupId: neu.setupId } : undefined,
    notes: neu.other || '',
    ...peopleFromAgri(neu),
})

const mapSprayingApplication = neu => ({
    kind: {
        applicationType: 'agrichemical',
        cropType: neu.cropType || '',
        targetPests: Array.isArray(neu.pests) ? neu.pests : [],
        targetWeeds: Array.isArray(neu.weeds) ? neu.weeds : [],
    },
    quantity: {
        areaSize: neu.areaSize ?? '',
        totalQtyUsed: neu.productAmount ?? '',
    },
    schedule: {
        startTime: neu.startTime || '',
        finishTime: neu.finishTime || '',
    },
    conditionsBefore: {
        temperature: neu.temperatureBefore ?? '',
        humidity: neu.humidityBefore ?? '',
        windSpeed: neu.windSpeedBefore ?? '',
        windDirection: neu.windDirectionBefore || '',
    },
    conditionsAfter: {
        temperature: neu.temperatureAfter ?? '',
        humidity: neu.humidityAfter ?? '',
        windSpeed: neu.windSpeedAfter ?? '',
        windDirection: neu.windDirectionAfter || '',
    },
    equipment: {
        boomHeight: neu.boomHeight ?? '',
        boomSpeed: neu.boomSpeed ?? '',
        nozzleName: neu.nozzleName || '',
        nozzleType: neu.nozzleType || '',
        sprayAngle: neu.sprayAngle ?? '',
    },
    manualProducts: Array.isArray(neu.products) ? neu.products.map(name => (typeof name === 'string' ? { name } : name)) : [],
    operator: neu.personName ? { name: neu.personName } : undefined,
})

const mapPestApplication = neu => ({
    kind: {
        applicationType: 'pestBait',
        cropType: '',
        targetPests: [],
        targetWeeds: [],
    },
    quantity: {
        areaSize: neu.areaSize ?? '',
        totalQtyUsed: neu.productAmount ?? '',
    },
    schedule: {
        startTime: neu.baitingStartTime || '',
        finishTime: neu.baitingEndTime || '',
    },
    productId: neu.productId ?? null,
    baitingRemoveTime: neu.baitingRemoveTime || '',
    checkDates: Array.isArray(neu.checkDates) ? neu.checkDates : [],
    locations: Array.isArray(neu.locations) ? neu.locations : [],
    notes: neu.other || '',
    ...peopleFromPest(neu),
})

const mapVetTreatment = neu => ({
    subtype: neu.type || '',
    productId: neu.productId ?? null,
    productAmount: neu.productAmount ?? '',
    batchNum: neu.batchNum || '',
    expiryDate: neu.expiryDate || '',
    whp: neu.whp ?? '',
    esi: neu.esi ?? '',
    species: neu.species || '',
    animalScope: neu.animalScope || '',
    animalGender: neu.animalGender || '',
    animalDOBStart: neu.animalDOBStart || '',
    animalDOBEnd: neu.animalDOBEnd || '',
    animalWeight: neu.animalWeight ?? '',
    applicatorName: neu.applicatorName || '',
    notes: neu.other || '',
    mobs: neu.mobs,
})

const resolveRouting = (row, defaultLegacyType) => {
    const rawType = row?.type || defaultLegacyType || ''
    const neu = row?.new || {}
    if (neu.type === 'command') {
        return { type: PUMP_COMMAND_EVENT_TYPE, branch: 'command', map: mapPumpCommand }
    }
    if (rawType === 'vetChemicalEvent') {
        return { type: ANIMAL_TREATMENT_TYPE, branch: 'treatment', map: mapVetTreatment }
    }
    if (rawType === 'pestChemicalEvent') {
        return { type: CHEM_SPRAY_APPLY.type, branch: APPLICATION_BRANCH, map: mapPestApplication }
    }
    if (rawType === 'sprayingEvent') {
        return { type: CHEM_SPRAY_APPLY.type, branch: APPLICATION_BRANCH, map: mapSprayingApplication }
    }
    // agriChemicalEvent and unknown spray-like defaults
    return { type: CHEM_SPRAY_APPLY.type, branch: APPLICATION_BRANCH, map: mapAgriApplication }
}

/**
 * @param {object} row v1 legacy record
 * @param {{ farmId?: number, legacySource?: string, defaultLegacyType?: string }} [ctx]
 */
export function normalizeLegacyChemicalEvent(row, { farmId, legacySource, defaultLegacyType } = {}) {
    if (!row || typeof row !== 'object') return null
    const neu = row.new || {}
    const { type, branch, map } = resolveRouting(row, defaultLegacyType)
    const branchPayload = map(neu)
    const farmIdNum = asId(farmId)
    const assetIds = asId(neu.asset) != null ? [asId(neu.asset)] : []
    const paddockIds = paddockIdsFromLegacyNew(neu)
    const animalIds = Array.isArray(row.animal_ids) ? row.animal_ids.map(String) : []

    const detail = {
        [branch]: branchPayload,
        legacy: {
            source: legacySource || '',
            rawType: row.type || defaultLegacyType || '',
            who: row.who || '',
            record: row,
        },
    }
    if (type === ANIMAL_TREATMENT_TYPE && neu.type) {
        detail.subtype = neu.type
    }

    return {
        id: row.id,
        type,
        time: normalizeEventTime(typeof row.timestamp === 'string' ? row.timestamp : row.timestamp ? new Date(row.timestamp).toISOString() : ''),
        farmIds: farmIdNum != null ? [farmIdNum] : [],
        assetIds,
        paddockIds,
        animalIds,
        userIds: [],
        detail,
        legacySource: legacySource || '',
        ...(Array.isArray(row.mob_ids) ? { mobIds: row.mob_ids } : {}),
    }
}

export function normalizeLegacyChemicalEvents(rows, ctx = {}) {
    if (!Array.isArray(rows)) return []
    return rows.map(row => normalizeLegacyChemicalEvent(row, ctx)).filter(Boolean)
}
